import React, { useState } from 'react';
import { ButtonSmallTable } from '../../../global/Styles/buttons';
import { ErrorToast, InfoToast, OkToast } from '../../Toast';
import { moneyFormat } from '../../../../utils/format';
import { checkAPI_URL } from '../../../../utils/environment';

const DetallesConciliacion = (props) => {
  const { detailsLoading, selectedProcess, detailFilter, detailRows, detailCounts } = props.data;
  const { handleDetailFilterChange, handleCloseDetails, handleRefreshDetails } = props.actions;

  const [validationLoading, setValidationLoading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmResult, setConfirmResult] = useState(null);

  const processId = selectedProcess?.id || selectedProcess?.ID || selectedProcess?.TAKEOFF_IMPORT_ID || '';
  const token = selectedProcess?.token || selectedProcess?.TOKEN || '';
  const estado = selectedProcess?.estado || selectedProcess?.ESTADO || '';

  const handleValidateImport = async () => {
    if (!processId) {
      ErrorToast('Error', 'No se encontró el ID del proceso');
      return;
    }

    setValidationLoading(true);
    try {
      const response = await fetch(`${checkAPI_URL()}/takeoffImports/${processId}/validar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token || localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({ tolerance: 0.01 }),
      });

      const data = await response.json();

      if (!response.ok) {
        ErrorToast('Error', data.message || 'Error desconocido');
        return;
      }

      setValidationResult(data);
      setShowValidationModal(true);

      // Actualizar los detalles si la función está disponible
      if (handleRefreshDetails) {
        handleRefreshDetails();
      }
    } catch (error) {
      ErrorToast('Error', 'No se pudo validar el import.');
      console.error('Error en validación:', error);
    } finally {
      setValidationLoading(false);
    }
  };

  const getValidationErrors = (result) => {
    if (!result) {
      return 0;
    }
    const raw =
      result.errores ??
      result.invalidas ??
      result.invalidas_count ??
      result.errors ??
      result.invalid_count ??
      0;
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const validationErrors = getValidationErrors(validationResult);
  const normalizedEstado = String(estado || '').toLowerCase();
  const isValidatedState = normalizedEstado.includes('validado') || normalizedEstado.includes('validada');
  const canConfirmImport = Boolean(validationResult) || isValidatedState;
  const confirmButtonClassName = `mb-0 ${
    canConfirmImport ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-500 opacity-60 cursor-not-allowed'
  }`;

  const handleOpenConfirmModal = () => {
    if (!canConfirmImport) {
      InfoToast({ title: 'Validacion', message: 'Primero valide el import.' });
      return;
    }
    setConfirmResult(null);
    setShowConfirmModal(true);
  };

  const handleConfirmImport = async () => {
    if (!processId) {
      ErrorToast('Error', 'No se encontró el ID del proceso');
      return;
    }
    if (!canConfirmImport) {
      InfoToast({ title: 'Validacion', message: 'Primero valide el import.' });
      return;
    }

    setConfirmLoading(true);
    try {
      const response = await fetch(`${checkAPI_URL()}/takeoffImports/${processId}/confirmar`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token || localStorage.getItem('token') || ''}`,
        },
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data?.success === false) {
        ErrorToast('Error', data.message || 'No se pudo confirmar el proceso.');
        return;
      }

      OkToast({ message: data.message || 'Proceso confirmado correctamente' });
      setConfirmResult(data);

      if (handleRefreshDetails) {
        handleRefreshDetails();
      }
    } catch (error) {
      ErrorToast('Error', 'No se pudo confirmar el proceso.');
      console.error('Error en confirmación:', error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const renderValido = (value) => {
    if (value === undefined || value === null) {
      return '';
    }
    if (typeof value === 'boolean') {
      return value ? 'Si' : 'No';
    }
    if (String(value) === '1') {
      return 'Si';
    }
    if (String(value) === '0') {
      return 'No';
    }
    return value;
  };

  const counts = detailCounts || { all: detailRows.length, valid: 0, invalid: 0 };
  const filters = [
    { key: 'all', label: `Todos (${counts.all ?? 0})` },
    { key: 'valid', label: `Validos (${counts.valid ?? 0})` },
    { key: 'invalid', label: `Invalidos (${counts.invalid ?? 0})` },
  ];

  return (
    <div
      className="min-w-screen animated fadeIn faster outline-none focus:outline-none fixed inset-0 left-0 top-0 z-50 flex h-screen items-center justify-center overflow-hidden bg-cover bg-center bg-no-repeat p-4"
      id="modal-id">
      <div className="fixed inset-0 z-0 bg-black opacity-50"></div>
      <div
        className="relative mx-auto my-auto w-full max-w-6xl overflow-hidden rounded-xl bg-white shadow-lg"
        style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div
          className="p-5 text-left"
          style={{ minHeight: 0, display: 'flex', flexDirection: 'column', flex: '1 1 auto' }}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-xl">Detalles de conciliacion</h2>
              <p className="text-sm text-gray-600">
                Proceso: {processId || 'N/A'} {token ? `- ${token}` : ''} {estado ? `(${estado})` : ''}
              </p>
            </div>
            <ButtonSmallTable
              onClick={handleCloseDetails}
              className="mb-0">
              Cerrar
            </ButtonSmallTable>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {filters.map((filter) => (
              <ButtonSmallTable
                key={filter.key}
                onClick={() => handleDetailFilterChange(filter.key)}
                className={`${detailFilter === filter.key ? 'bg-gray-800' : ''} mb-0`}>
                {filter.label}
              </ButtonSmallTable>
            ))}
            <ButtonSmallTable
              onClick={handleValidateImport}
              disabled={validationLoading}
              className="mb-0 bg-blue-600 hover:bg-blue-700">
              {validationLoading ? 'Validando...' : 'Validar Import'}
            </ButtonSmallTable>
            <ButtonSmallTable
              onClick={handleOpenConfirmModal}
              disabled={!canConfirmImport || confirmLoading}
              className={confirmButtonClassName}
              title={
                !canConfirmImport
                  ? 'Primero valide el import'
                  : validationResult && validationErrors > 0
                  ? 'La validacion tiene errores'
                  : ''
              }>
              {confirmLoading ? 'Confirmando...' : 'Confirmar Import'}
            </ButtonSmallTable>
          </div>

          <div
            className="mt-4"
            style={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto' }}>
            {detailsLoading ? (
              <div className="py-10 text-center text-sm text-gray-500">Cargando detalles...</div>
            ) : (
              <table className="w-full table-auto text-sm">
                <thead className="sticky top-0 z-10 bg-gray-100 text-sm uppercase text-gray-700 ">
                  <tr>
                    <th className="px-1 text-center text-sm font-semibold uppercase">#</th>
                    <th className="px-1 text-left text-sm font-semibold uppercase">No. de Reporte</th>
                    <th className="px-1 text-right text-sm font-semibold uppercase">Valor Tarifa</th>
                    <th className="px-1 text-right text-sm font-semibold uppercase">Fraccion</th>
                    <th className="px-1 text-right text-sm font-semibold uppercase">Total</th>
                    <th className="px-1 text-center text-sm font-semibold uppercase">Valido</th>
                    <th className="px-1 text-left text-sm font-semibold uppercase">Observaciones</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  {detailRows.length === 0 ? (
                    <tr>
                      <td
                        className="px-2 py-3 text-center text-sm text-gray-500"
                        colSpan={7}>
                        Sin registros
                      </td>
                    </tr>
                  ) : (
                    detailRows.map((row, index) => {
                      const reporte = row.REPORTE || row.NO_REPORTE || row.reporte || row.reporte_numero || '';
                      const valorTarifa = row.VALOR_TARIFA || row.valor_tarifa || row.valor || 0;
                      const fraccion = row.FRACCION || row.fraccion || 0;
                      const total = row.TOTAL || row.total || 0;
                      const valido = row.VALIDO ?? row.valido ?? row.is_valid ?? row.valid;

                      // Extraer errores del API
                      let erroresTexto = '';
                      if (row.ERROR_MSG) {
                        erroresTexto = row.ERROR_MSG;
                      } else if (row.ERROR_JSON?.rules_failed && Array.isArray(row.ERROR_JSON.rules_failed)) {
                        erroresTexto = row.ERROR_JSON.rules_failed.join('; ');
                      } else {
                        const errores = row.errores || row.errors || row.OBSERVACIONES || row.observaciones || '';
                        erroresTexto = Array.isArray(errores) ? errores.join(', ') : errores;
                      }

                      const isInvalid = valido === 0 || valido === '0' || valido === false;
                      const rowClassName = isInvalid ? 'odd:bg-red-50 even:bg-red-100 hover:bg-red-150' : 'odd:bg-white even:bg-gray-50 hover:bg-gray-100';

                      return (
                        <tr
                          key={index}
                          className={rowClassName}>
                          <td className="px-1 text-center">{index + 1}</td>
                          <td className="px-1 text-left">{reporte}</td>
                          <td className="px-1 text-right">{moneyFormat(valorTarifa)}</td>
                          <td className="px-1 text-right">{fraccion}</td>
                          <td className="px-1 text-right">{moneyFormat(total)}</td>
                          <td className="px-1 text-center">{renderValido(valido)}</td>
                          <td className={`px-1 text-left ${isInvalid ? 'font-semibold text-red-700' : ''}`}>{erroresTexto}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Resultados de Validación */}
      {showValidationModal && validationResult && (
        <div className="min-w-screen animated fadeIn faster outline-none focus:outline-none fixed inset-0 left-0 top-0 z-50 flex h-screen items-center justify-center overflow-hidden bg-cover bg-center bg-no-repeat p-4">
          <div className="fixed inset-0 z-0 bg-black opacity-50"></div>
          <div className="relative mx-auto my-auto w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-lg">
            <div className="p-6">
              <div className="mb-6 flex items-center justify-between gap-4">
                <h2 className="text-2xl font-bold">Resultados de Validación</h2>
                <button
                  onClick={() => setShowValidationModal(false)}
                  className="text-2xl font-bold text-gray-500 hover:text-gray-700">
                  ×
                </button>
              </div>

              <div className="mb-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded bg-gray-100 p-4">
                    <p className="text-sm text-gray-600">Import ID</p>
                    <p className="text-lg font-semibold">{validationResult.import_id}</p>
                  </div>
                  <div className="rounded bg-gray-100 p-4">
                    <p className="text-sm text-gray-600">Total de Filas</p>
                    <p className="text-lg font-semibold">{validationResult.total_rows}</p>
                  </div>
                  <div className="rounded bg-green-100 p-4">
                    <p className="text-sm text-gray-600">Válidas</p>
                    <p className="text-lg font-semibold text-green-600">{validationResult.validas}</p>
                  </div>
                  <div className="rounded bg-red-100 p-4">
                    <p className="text-sm text-gray-600">Errores</p>
                    <p className="text-lg font-semibold text-red-600">{validationResult.errores}</p>
                  </div>
                  <div className="col-span-2 rounded bg-gray-100 p-4">
                    <p className="text-sm text-gray-600">Tolerancia Utilizada</p>
                    <p className="text-lg font-semibold">{validationResult.tolerance_used}</p>
                  </div>
                  <div className="col-span-2 rounded bg-gray-100 p-4">
                    <p className="text-sm text-gray-600">Tiempo de Ejecución</p>
                    <p className="text-lg font-semibold">{validationResult.execution_time_ms}ms</p>
                  </div>
                </div>

                {validationResult.message && (
                  <div className="rounded border border-blue-400 bg-blue-100 px-4 py-3 text-blue-700">
                    <p className="font-semibold">Mensaje</p>
                    <p>{validationResult.message}</p>
                  </div>
                )}

                {validationErrors > 0 && (
                  <div className="rounded border border-red-300 bg-red-50 px-4 py-3 text-red-700">
                    <p className="font-semibold">Validacion con errores</p>
                    <p>Revise antes de confirmar.</p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <ButtonSmallTable
                  onClick={handleOpenConfirmModal}
                  disabled={!canConfirmImport || confirmLoading}
                  className={confirmButtonClassName}>
                  {confirmLoading ? 'Confirmando...' : 'Confirmar'}
                </ButtonSmallTable>
                <ButtonSmallTable onClick={() => setShowValidationModal(false)}>Cerrar</ButtonSmallTable>
              </div>
            </div>
          </div>
        </div>
      )}

      {showConfirmModal && (
        <div className="min-w-screen animated fadeIn faster outline-none focus:outline-none fixed inset-0 left-0 top-0 z-50 flex h-screen items-center justify-center overflow-hidden bg-cover bg-center bg-no-repeat p-4">
          <div className="fixed inset-0 z-0 bg-black opacity-50"></div>
          <div className="relative mx-auto my-auto w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-lg">
            <div className="p-6">
              <div className="mb-4 flex items-center justify-between gap-4">
                <h2 className="text-xl font-bold">Confirmar import</h2>
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="text-2xl font-bold text-gray-500 hover:text-gray-700">
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <div className="rounded bg-gray-100 p-4">
                  <p className="text-sm text-gray-600">Proceso</p>
                  <p className="text-lg font-semibold">
                    {processId || 'N/A'} {token ? `- ${token}` : ''}
                  </p>
                </div>

                {confirmResult ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded bg-green-100 p-3">
                        <p className="text-xs text-gray-600">Insertadas</p>
                        <p className="text-lg font-semibold text-green-700">{confirmResult.inserted}</p>
                      </div>
                      <div className="rounded bg-yellow-100 p-3">
                        <p className="text-xs text-gray-600">Omitidas</p>
                        <p className="text-lg font-semibold text-yellow-700">{confirmResult.skipped}</p>
                      </div>
                      <div className="rounded bg-red-100 p-3">
                        <p className="text-xs text-gray-600">Invalidas</p>
                        <p className="text-lg font-semibold text-red-700">{confirmResult.invalidated}</p>
                      </div>
                      <div className="rounded bg-gray-100 p-3">
                        <p className="text-xs text-gray-600">Lote</p>
                        <p className="text-lg font-semibold">{confirmResult.lote_id}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded bg-green-100 p-3">
                        <p className="text-xs text-gray-600">Validas</p>
                        <p className="text-lg font-semibold text-green-700">{confirmResult.validas}</p>
                      </div>
                      <div className="rounded bg-red-100 p-3">
                        <p className="text-xs text-gray-600">Errores</p>
                        <p className="text-lg font-semibold text-red-700">{confirmResult.errores}</p>
                      </div>
                    </div>
                    {confirmResult.message && (
                      <div className="rounded border border-blue-200 bg-blue-50 p-4 text-blue-700">
                        <p className="text-sm font-semibold">Mensaje</p>
                        <p className="text-sm">{confirmResult.message}</p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {validationResult ? (
                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded bg-green-100 p-3">
                          <p className="text-xs text-gray-600">Validas</p>
                          <p className="text-lg font-semibold text-green-700">{validationResult.validas}</p>
                        </div>
                        <div className="rounded bg-red-100 p-3">
                          <p className="text-xs text-gray-600">Errores</p>
                          <p className="text-lg font-semibold text-red-700">{validationResult.errores}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="rounded border border-blue-200 bg-blue-50 p-4 text-blue-700">
                        <p className="text-sm font-semibold">Proceso validado</p>
                        <p className="text-sm">Se confirmara el import con el estado actual.</p>
                      </div>
                    )}

                    {validationErrors > 0 && (
                      <div className="rounded border border-red-300 bg-red-50 p-4 text-red-700">
                        <p className="text-sm font-semibold">Validacion con errores</p>
                        <p className="text-sm">Confirme solo si desea continuar.</p>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <ButtonSmallTable
                  onClick={() => setShowConfirmModal(false)}
                  disabled={confirmLoading}
                  className="mb-0">
                  {confirmResult ? 'Cerrar' : 'Cancelar'}
                </ButtonSmallTable>
                {!confirmResult && (
                  <ButtonSmallTable
                    onClick={handleConfirmImport}
                    disabled={confirmLoading}
                    className="mb-0 bg-green-600 hover:bg-green-700">
                    {confirmLoading ? 'Confirmando...' : 'Confirmar'}
                  </ButtonSmallTable>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DetallesConciliacion;
